<script>
import { templateRef, useElementVisibility, useEventBus } from '@vueuse/core';
import { useHead } from '@vueuse/head';
import configure from 'instantsearch.js/es/widgets/configure/configure';
import { storeToRefs } from 'pinia';
import {
  computed,
  createSSRApp,
  defineComponent,
  getCurrentInstance,
  getCurrentScope,
  onBeforeMount,
  onMounted,
  onServerPrefetch,
  provide,
  ref,
  useSSRContext,
  watch,
} from 'vue';
import { renderToString } from 'vue/server-renderer';
import { AisConfigure, AisInstantSearchSsr, AisStats } from 'vue-instantsearch/vue3/es';
import { useRoute, useRouter } from 'vue-router';

import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import CategoryDescription from '@/components/category/Description.vue';
import CategoryDescriptionGlutenFree from '@/components/category/DescriptionGlutenFree.vue';
import SamplesTopDescription from '@/components/category/SamplesTopDescription.vue';
import CmsContent from '@/components/cms/CmsContent.vue';
import BannerCard from '@/components/search/products/BannerCard.vue';
import ResultCard from '@/components/search/products/ResultCard.vue';
import ResultCardList from '@/components/search/products/ResultCardList.vue';
import HiddenMobileFiltersTrigger from '@/components/search/widgets/HiddenMobileFiltersTrigger.vue';
import HitsWithMarketingBanners from '@/components/search/widgets/HitsWithMarketingBanners.vue';
import NoResults from '@/components/search/widgets/NoResults.vue';
import PaginatedResultsCount from '@/components/search/widgets/PaginatedResultsCount.vue';
import Pagination from '@/components/search/widgets/Pagination.vue';
import RefinementControlPanel from '@/components/search/widgets/RefinementControlPanel.vue';
import RevealMobileFilters from '@/components/search/widgets/RevealMobileFilters.vue';
import SearchBox from '@/components/search/widgets/SearchBox.vue';
import SortBy from '@/components/search/widgets/SortBy.vue';
import { useCms } from '@/composables/useCms';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import chevronDown from '@/icons/chevronDown';
import { ALGOLIA_USER_TOKEN_COOKIE } from '@/lib/personalization/common';
import routerLinkDelegationMixin from '@/mixins/routerLinkDelegation';
import { useCategory } from '@/stores/category';
import { gtag } from '@/utils/analytics';
import { trackAlgoliaExperiment } from '@/utils/analytics/experimentViewedEvent';
import { sendProductsSearched } from '@/utils/analytics/productSearched';
import { createSearchApp } from '@/utils/createInstantSearch';
import { getCookie } from '@/utils/isomorphic/cookie';
import { createAlgoliaRouter } from '@/utils/search/createAlgoliaRouter';
import { plpRefinementMenus } from '@/utils/searchRefinementMenus';

export default defineComponent({
  name: 'PLP',
  props: {
    ancestors: { required: false, type: Array, default: () => [] },
    categoryKey: { required: false, type: String, default: '' },
    tags: { required: false, type: Array, default: () => [] },
    metaData: { required: false, type: Object, default: () => ({}) },
    presetFilters: { required: false, type: Array, default: () => [] },
    presetTitle: { required: false, type: String, default: '' },
    whitelistedFacets: { required: false, type: Array, default: () => [] },
    departmentHierarchy: { required: false, type: Array, default: () => [] },
  },
  components: {
    AisConfigure,
    AisInstantSearchSsr,
    AisStats,
    BannerCard,
    Breadcrumb,
    CategoryDescription,
    CategoryDescriptionGlutenFree,
    CmsContent,
    HiddenMobileFiltersTrigger,
    HitsWithMarketingBanners,
    NoResults,
    PaginatedResultsCount,
    Pagination,
    RefinementControlPanel,
    ResultCard,
    ResultCardList,
    RevealMobileFilters,
    SamplesTopDescription,
    SearchBox,
    SortBy,
  },
  mixins: [routerLinkDelegationMixin],
  setup(props) {
    const router = useRouter();
    const route = useRoute();
    useHead(computed(() => props.metaData));

    const categoryStore = useCategory();
    const { category } = storeToRefs(categoryStore);
    const { flags, loadDyFlags } = useFeatureFlags();

    const glutenFreeOnly = computed(
      () =>
        route.path.startsWith('/gluten-free') &&
        props.tags.some((tag) => tag.tagName === 'gluten-free'),
    );
    const samplesOnly = computed(() => props.presetFilters.includes('traits:sample'));

    const breadcrumbs = computed(() => props.ancestors.filter((a) => a.url !== null));
    const breadcrumbEnd = computed(() => {
      const last = props.ancestors.find((a) => a.url === null);
      return last ? last.name : props.tags.map((t) => t.tagName).join(' / ');
    });
    const categoryName = computed(() => category.value.name);
    const categoryDescription = computed(() => category.value.description);
    const topDescription = computed(() => category.value.topDescription);
    const wholesaleDescription = computed(() => category.value.wholesaleDescription);
    const description = computed(() =>
      props.presetFilters.includes('traits:wholesale') && wholesaleDescription.value
        ? wholesaleDescription.value
        : categoryDescription.value,
    );
    const descriptionComponent = computed(() =>
      glutenFreeOnly.value ? 'CategoryDescriptionGlutenFree' : 'CategoryDescription',
    );
    const topDescriptionComponent = computed(() =>
      samplesOnly.value ? 'SamplesTopDescription' : 'CategoryDescription',
    );
    const pageTitle = computed(() => {
      if (props.presetTitle) return props.presetTitle;
      if (props.presetFilters.includes('traits:wholesale')) return `Bulk ${categoryName.value}`;
      if (glutenFreeOnly.value) return `Gluten-Free ${categoryName.value}`;
      if (samplesOnly.value) return 'Yummy Samples';
      if (!props.categoryKey && props.tags.length)
        return props.tags.map((t) => t.tagName).join(' / ');
      return categoryName.value;
    });

    const analyticsTags = computed(() => {
      if (!props.categoryKey) return ['Tag PLP'];
      return ['PLP', props.categoryKey, `PLP: ${props.departmentHierarchy.join(' > ')}`];
    });

    const goToSearch = (query) => {
      router.push({ name: 'Search', query: { query } });
    };

    // Pre-filtering logic
    const categoryFacet = computed(() =>
      props.categoryKey ? `Product.categoryKeys:${props.categoryKey}` : undefined,
    );
    const refinementMenus = computed(() =>
      plpRefinementMenus.filter((menuObj) => {
        if (!menuObj.conditional) return true;
        return props.whitelistedFacets.includes(menuObj.attribute) && props.categoryKey;
      }),
    );
    function facetFromTagReference(tagReference) {
      let groupNameKey = tagReference.facetGroup;
      if (groupNameKey !== 'tags') groupNameKey = tagReference.facetGroup.toLowerCase();
      const facetsPreface = groupNameKey === 'tags' ? '' : 'facets.';
      groupNameKey = `Product.${facetsPreface + groupNameKey}`;
      return groupNameKey;
    }
    const facetsRefinementsByType = computed(() => {
      const refinements = {
        disjunctiveFacetsRefinements: {},
        facetsRefinements: {},
        hierarchicalFacetsRefinements: {},
      };
      props.tags
        .filter(({ hidden, showOnSearchPage }) => !hidden && showOnSearchPage)
        .forEach((tag) => {
          const { tagName } = tag;
          const facet = facetFromTagReference(tag);
          const menuWidgetObj = refinementMenus.value.find((m) => m.attribute === facet);
          if (!menuWidgetObj) return;
          const { operator: tagsOperator } = menuWidgetObj;
          const refinementGroup =
            tagsOperator === 'and' ? 'facetsRefinements' : 'disjunctiveFacetsRefinements';
          if (!(facet in refinements[refinementGroup])) {
            refinements[refinementGroup][facet] = [];
          }
          refinements[refinementGroup][facet].push(tagName);
        });
      return refinements;
    });
    const facetsToRetrieve = computed(() => {
      const facets = {
        disjunctiveFacets: [],
        facets: [],
        hierarchicalFacets: [],
      };
      refinementMenus.value.forEach(({ attribute, operator, isHierarchical = false }) => {
        if (operator === 'and') {
          if (isHierarchical) {
            facets.hierarchicalFacets.push({
              name: attribute,
              attributes: [attribute],
            });
          } else facets.facets.push(attribute);
        } else if (operator === 'or') {
          facets.disjunctiveFacets.push(attribute);
        }
      });
      return facets;
    });
    const requiresCategoryKeyFilter = computed(
      () => !!props.categoryKey && !props.presetFilters.includes('traits:snackPack'),
    );

    const filters = computed(() => {
      if (!props.presetFilters.length) {
        const filterSegments = [categoryFacet.value ?? 'traits:searchable', 'traits:primary'];
        props.tags.forEach((t) => {
          const facet = facetFromTagReference(t);
          if (facet.includes(' ')) return;
          const menuWidgetObj = refinementMenus.value.find((m) => m.attribute === facet);
          // TODO: remove this check as part of WS-7355, when we stop managing `facetsRefinements`; just always push the tag
          if (!menuWidgetObj) filterSegments.push(`${facet}:"${t.tagName}"`);
        });
        return filterSegments.join(' AND ');
      }
      let combinedFilters = props.presetFilters.join(' AND ');
      if (requiresCategoryKeyFilter.value) {
        combinedFilters = `${categoryFacet.value} AND ${combinedFilters}`;
      }
      props.tags.forEach((t) => {
        const facet = facetFromTagReference(t);
        if (facet.includes(' ')) return;
        const menuWidgetObj = refinementMenus.value.find((m) => m.attribute === facet);
        // TODO: remove this check as part of WS-7355, when we stop managing `facetsRefinements`; just always push the tag
        if (!menuWidgetObj) combinedFilters += ` AND ${facet}:"${t.tagName}"`;
      });
      return combinedFilters;
    });
    const ruleContexts = computed(() => {
      const contexts = [];

      if (props.presetFilters.length) {
        contexts.push('Specialty_PLP');
        if (props.presetFilters.includes('traits:wholesale')) {
          let context = 'Wholesale';
          if (props.presetFilters.includes('traits:snackPack')) {
            context += '_Snack_Pack';
          } else {
            contexts.push(`${props.categoryKey}-wholesale`);
          }
          contexts.push(context);
        } else if (props.presetFilters.includes('traits:snackPack')) {
          contexts.push('Snack_Pack');
        } else if (samplesOnly.value) {
          contexts.push('Samples');
        }
      }

      if (props.tags.length) {
        const type = categoryFacet.value ? 'Category' : 'Browse';
        contexts.push(`Tag_${type}_PLP`);
        const combinedTags = props.tags
          .map((t) =>
            t.tagName
              .replace(/\s/g, '_')
              .replace(/[()]/g, '')
              .replace(/%/g, 'percent')
              .replace(/&/g, 'and'),
          )
          .join('_');
        contexts.push(`tag-${combinedTags}`);
        if (categoryFacet.value) contexts.push(props.categoryKey);
      }

      if (!contexts.length) {
        contexts.push('PLP');
        contexts.push(props.categoryKey);
      }

      return contexts;
    });

    const refinementControlPanel = ref();
    const openOverlay = (panel) => {
      refinementControlPanel.value.openOverlay(panel);
    };
    const trackAnalytics = computed(() => !!route.query.query);

    const queryModel = ref(route.query.query ?? '');

    const categoryBottomShelf = templateRef('categoryBottomShelf');
    const categoryBottomShelfVisible = useElementVisibility(categoryBottomShelf);
    const { content: cmsBottomShelf, loadCmsContent: loadCmsBottomShelf } = useCms(
      'category-bottom-shelf',
      {
        contentVisible: () => categoryBottomShelfVisible.value,
        prefetch: 'setup',
      },
    );
    const categoryTopShelf = templateRef('categoryTopShelf');
    const categoryTopShelfVisible = useElementVisibility(categoryTopShelf);
    const { content: cmsTopShelf, loadCmsContent: loadCmsTopShelf } = useCms('category-top-shelf', {
      contentVisible: () => categoryTopShelfVisible.value,
      prefetch: 'setup',
    });

    const hitsContainer = ref();
    const mobileFiltersATF = ref();

    const state = {
      analyticsTags,
      breadcrumbs,
      breadcrumbEnd,
      categoryFacet,
      categoryDescription,
      categoryName,
      goToSearch,
      chevronDown,
      cmsBottomShelf,
      cmsTopShelf,
      description,
      descriptionComponent,
      facetsRefinementsByType,
      facetsToRetrieve,
      filters,
      flags,
      hitsContainer,
      hitsPerPage: 60,
      glutenFreeOnly,
      loadCmsBottomShelf,
      loadCmsTopShelf,
      mobileFiltersATF,
      openOverlay,
      pageTitle,
      queryModel,
      refinementControlPanel,
      refinementMenus,
      requiresCategoryKeyFilter,
      route,
      ruleContexts,
      samplesOnly,
      searchInstanceKey: 'plp',
      sortByItems: [
        { value: 'Products', label: 'Popularity' },
        { value: 'ProductsByPriceDesc', label: 'Price high to low' },
        { value: 'ProductsByPriceAsc', label: 'Price low to high' },
        { value: 'ProductsByActivatedAtDesc', label: 'Newest' },
      ],
      topDescription,
      topDescriptionComponent,
      trackAnalytics,
      userToken: getCookie(ALGOLIA_USER_TOKEN_COOKIE, false),
      whitelistedRefinements: ['Product.categoryKeys'],
      wholesaleDescription,
    };

    const algoliaRouter = createAlgoliaRouter(router);
    const cloneComponent = (componentInstance, { mixins = [] } = {}) => {
      const options = {
        serverPrefetch: undefined,
        setup: () => {
          provide('$_ais_ssrInstantSearchInstance', state.instantsearch);
          return state;
        },
        fetch: undefined,
        _base: undefined,
        name: 'ais-ssr-root-component',
      };
      const appOptions = { ...componentInstance.$options, ...options };
      appOptions.mixins = [...mixins, routerLinkDelegationMixin];
      const app = createSSRApp(appOptions, props).provide('is-ais-clone', true).use(router);

      return app;
    };
    const mixin = createSearchApp('Products', algoliaRouter, cloneComponent);
    const { instantsearch } = mixin.data();
    state.instantsearch = instantsearch;
    provide('$_ais_ssrInstantSearchInstance', instantsearch);

    const component = getCurrentInstance()?.proxy;
    const ssrContext = import.meta.env.SSR ? useSSRContext() : undefined;
    onServerPrefetch(async () => {
      instantsearch.addWidgets([
        configure({
          attributesToRetrieve: [
            '*', // retrieves all attributes
            '-Product.shortDescription', // except this list of attributes (starting with a '-')
          ],
        }),
      ]);

      const scope = getCurrentScope();
      await Promise.all([
        categoryStore.loadCategory(props.categoryKey),
        instantsearch
          .findResultsState({
            component,
            renderToString,
          })
          .then((algoliaState) => {
            ssrContext.algoliaState = algoliaState;
          }),
      ]);

      // force all computed refs to recompute, preventing stale data triggered by first render
      // during the `instantsearch.findResultsState` call above polluting the final render
      scope?.effects?.forEach((effect) => {
        // eslint-disable-next-line no-underscore-dangle, no-param-reassign
        if (effect.computed) effect.computed._dirty = true;
      });
    });

    onBeforeMount(() => {
      const { __ALGOLIA_STATE__: algoliaState } = window;
      if (algoliaState) {
        instantsearch.hydrate(algoliaState);
        // eslint-disable-next-line no-underscore-dangle
        delete window.__ALGOLIA_STATE__;
      }
    });

    onMounted(async () => {
      loadDyFlags('[A/B Test] PPA Phase 2', ['pricePackPhase2'], {
        skipExperimentEvent: true,
      });

      window.dataLayer.push({ GDR: { pagetype: 'department' } });
      const forceRefresh = props.categoryKey !== category.value.key;
      categoryStore.loadCategory(props.categoryKey, { forceRefresh });

      // TODO: replace this with `criticalData()` when it is navigation-blocking
      const eventBus = useEventBus('delegated-router-nav-guards');
      const beforeRouteUpdate = async (to, from, next) => {
        const { categoryKey } = to.matched[0].props.default;
        const refresh = to.path !== from.path;
        const promises = [categoryStore.loadCategory(categoryKey, { forceRefresh: refresh })];
        if (refresh) {
          promises.push(loadCmsTopShelf(to.path, to.query), loadCmsBottomShelf(to.path, to.query));
        }
        await Promise.all(promises);
        next();
      };
      eventBus.on((event, { to, from, next }) => {
        if (event !== 'PLP' || to.path === from.path) return next();
        return beforeRouteUpdate(to, from, next);
      });

      let previousQuery = route.query.query ?? '';
      watch(route, (r) => {
        if (r.name === 'Search') return;

        const { query } = r.query;
        if (previousQuery !== query) {
          previousQuery = query;
          sendProductsSearched({ query, pageSection: 'PLP Search Bar' });
        }
        if (query !== queryModel.value) {
          queryModel.value = query ?? '';
        }

        const algoliaResults = instantsearch.renderState?.Products.hits?.results;
        if (r.query.refinementList) {
          const activeFilters = [];
          Object.keys(r.query.refinementList).forEach((key) => {
            r.query.refinementList[key].forEach((value) =>
              activeFilters.push(`${key.split('.').pop()}:${value}`),
            );
          });
          gtag('event', 'apply_filter', {
            indexName: r.query.sortBy ?? 'Products',
            filters: activeFilters,
            queryID: algoliaResults?.queryID,
          });
        }
        trackAlgoliaExperiment(algoliaResults);
      });
    });

    return state;
  },
});
</script>

<template>
  <div
    class="px-4 pt-4 pb-6 mx-auto max-w-screen-2xl lg:pt-6 plp xl:px-10"
    data-test="plp"
    itemscope
    itemtype="http://schema.org/ItemList"
  >
    <div class="-mx-4">
      <Breadcrumb :ancestors="breadcrumbs" class="pb-3 ws-md:pb-5" scrollPaddingClasses="px-4">
        <span
          v-if="breadcrumbEnd"
          aria-current="page"
          class="inline-block py-1 text-xs font-bold sm:text-sm"
        >
          {{ breadcrumbEnd }}
        </span>
      </Breadcrumb>
    </div>
    <div v-once id="plp-above-title" />
    <h1>{{ pageTitle }}</h1>
    <CmsContent :content="cmsTopShelf" model="category-top-shelf" ref="categoryTopShelf" />
    <Component
      v-if="topDescription || samplesOnly"
      class="formatted-content top-description"
      data-test="top-description"
      :description="topDescription"
      :is="topDescriptionComponent"
      @click="delegateLinksToRouter"
    />
    <div>
      <div class="w-full">
        <AisInstantSearchSsr>
          <div
            class="grid grid-cols-1 gap-0 md:grid-cols-3 md:gap-4 ws-md:grid-cols-4 xl:grid-cols-5"
          >
            <RefinementControlPanel
              panel-group-locator="filtering-panel"
              ref="refinementControlPanel"
              :refinement-menus="refinementMenus"
              :sort-by-items="sortByItems"
              :whitelisted-refinements="whitelistedRefinements"
            >
              <template v-slot:search-bar>
                <SearchBox
                  v-model="queryModel"
                  class="md:hidden lg:block"
                  :placeholder="`Search within ${categoryName}`"
                />
                <SearchBox
                  v-model="queryModel"
                  class="block lg:hidden"
                  placeholder="Search within..."
                />
              </template>
            </RefinementControlPanel>
            <div class="md:hidden">
              <RevealMobileFilters
                class="mb-2"
                data-test="reveal-filters-mobile"
                :separateFilterSort="flags.separateFilterSortPLP"
                :sortApplied="route.query.sortBy"
                :sortOptions="sortByItems"
                ref="mobileFiltersATF"
                @open-filters="openOverlay"
                @open-separated-filters="openOverlay('filters')"
                @open-sort-by="openOverlay('sort')"
              />
            </div>
            <div class="md:col-span-2 ws-md:col-span-3 xl:col-span-4">
              <div class="flex">
                <div class="w-1/4 md:w-1/2">
                  <AisStats>
                    <template v-slot:default="{ nbHits }">
                      <span class="md:px-4 ais-Stats-text" data-test="stats-text-top">
                        {{ nbHits }} results
                      </span>
                    </template>
                  </AisStats>
                  <AisStats>
                    <template v-slot:default="{ nbHits }">
                      <meta itemprop="numberOfItems" :content="nbHits" />
                    </template>
                  </AisStats>
                </div>
                <div class="w-3/4 text-right md:w-1/2">
                  <div class="hidden md:inline ptn-filter-control exclusive sort-by">
                    <p>Sort by:</p>
                    <SortBy :items="sortByItems" data-test="sort-by" />
                  </div>
                </div>
              </div>
              <div ref="hitsContainer">
                <HitsWithMarketingBanners :initial-refinements="facetsRefinementsByType">
                  <template v-slot:default="{ items }">
                    <ResultCardList
                      class="grid grid-cols-2 ws-md:grid-cols-3 xl:grid-cols-4"
                      data-test="products-list"
                      :items
                      v-slot="{ item }"
                    >
                      <meta itemprop="position" :content="item.__position" />
                      <BannerCard v-if="'banner' in item" :item />
                      <ResultCard
                        v-else
                        :departmentHierarchy
                        :item
                        layout="plp"
                        page="PLP"
                        :query="queryModel"
                        :tags
                        :title="pageTitle"
                        trackingEventLocation="subcategory"
                      />
                    </ResultCardList>
                    <NoResults class="flex w-full" @select-search-term="goToSearch" />
                  </template>
                </HitsWithMarketingBanners>
              </div>
              <div class="flex-row flex-wrap results-footer">
                <div class="flex w-full md:w-3/4">
                  <Pagination :show-first="false" :show-last="false" data-test="pagination" />
                </div>
                <div class="flex justify-end w-full md:w-1/4">
                  <PaginatedResultsCount />
                </div>
              </div>
              <HiddenMobileFiltersTrigger
                v-if="!flags.separateFilterSortPLP"
                class="fixed md:hidden"
                :hitsContainer
                :primaryFiltersButton="mobileFiltersATF"
                @show-filters="openOverlay"
              />
            </div>
          </div>
          <AisConfigure
            :analyticsTags
            :clickAnalytics="true"
            :disjunctiveFacetsRefinements="facetsRefinementsByType.disjunctiveFacetsRefinements"
            :facetsRefinements="facetsRefinementsByType.facetsRefinements"
            :filters
            :getRankingInfo="true"
            :hierarchicalFacetsRefinements="facetsRefinementsByType.hierarchicalFacetsRefinements"
            :hitsPerPage
            :ruleContexts
            :userToken
          />
        </AisInstantSearchSsr>
      </div>
    </div>
    <CmsContent :content="cmsBottomShelf" model="category-bottom-shelf" ref="categoryBottomShelf" />
    <Component
      v-if="description"
      class="formatted-content description"
      data-test="bottom-description"
      :description
      :is="descriptionComponent"
      @click="delegateLinksToRouter"
    />
  </div>
</template>

<style lang="scss" scoped>
.formatted-content {
  &:deep(p) {
    @include respond-max($screen-xs-max) {
      font-size: 16px;
    }
  }
  &.description {
    margin-bottom: 20px;
    @include respond-min($screen-md-min) {
      margin-top: 40px;
    }
  }
}
.top-description {
  margin-bottom: 20px;
  @include respond-min($screen-md-min) {
    margin-bottom: 40px;
  }
}

.hidden {
  visibility: visible;
}
</style>
